import {
	Matrix4,
	Raycaster,
	MathUtils,
	Quaternion,
	Mesh,
	SphereGeometry,
	MeshBasicMaterial,
	Vector3,
} from 'three';
import { getIntersectedMarkerObject } from '@3jss/Scene/interactionHelpers';
import { browserName } from 'react-device-detect';


const TYPES_TO_IGNORE = ['Sprite', 'Line'];

const NAMES_TO_IGNORE = [
	'right',
	'left',
	'XRController',
	'XRControllerGrip',
	'XRHand',
	'line',
	'collision_mesh',
];


export const threeEditorVREvents = (
	renderer,
	scene,
	xrControls,
	xrGripControls,
	xrHands,
	camera,
	dolly,
	onMouseUpCallback,
) => {
	const raycaster = new Raycaster();
	const tempMatrix = new Matrix4();

	const controllerData = {
		intersections: [],
		selectPressed: false,
		selectedObject: null,
		hoveredMarker: null,
		teleportMarker: null,
		isDraggingWithJoystick: false,
	};

	const controllerIntersectionsData = {
		left: Object.create(controllerData),
		right: Object.create(controllerData),
		'transient-pointer': Object.create(controllerData),
		camera: Object.create(controllerData),
	};


	const getIntersections = (object, scene, camera) => {
		tempMatrix.identity().extractRotation(object.matrixWorld);
		if (camera) raycaster.camera = camera;

		raycaster.ray.origin.setFromMatrixPosition(object.matrixWorld);
		raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);


		// Three js has issues raycasting sprites, those and camera are removed for now

		return raycaster
			?.intersectObjects(scene.children, true)
			.filter(
				(i) =>
					!TYPES_TO_IGNORE.includes(i.object.type) &&
					!NAMES_TO_IGNORE.includes(i.object.name),
			);
	};

	const handleAVPCameraGaze = () => {
		if (!renderer.xr.isPresenting || browserName != 'Safari') {
			return;
		}

		const intersects = getIntersections(camera, scene, camera);

		handleHover(intersects, 'camera');
	};

	const resetHovers = (source) => {
		controllerIntersectionsData[source].hoveredMarker?.onUnhover &&
			controllerIntersectionsData[source].hoveredMarker.onUnhover();
		controllerIntersectionsData[source].hoveredMarker = null;
	};

	const handleHover = (intersections, hoverSource) => {
		const sceneObject = getIntersectedMarkerObject(intersections);
		const marker = sceneObject?.object;
		if (marker) {
			controllerIntersectionsData[hoverSource].hoveredMarker = marker;
			marker.onHover && marker.onHover();
		}

		if (!marker && controllerIntersectionsData[hoverSource].hoveredMarker) {
			resetHovers(hoverSource);
		}
	};

	const handleController = (controller) => {
		// Update the intersections data for the controller
		const intersections = getIntersections(controller, scene, camera);

		if (!intersections) return;

		if (controllerIntersectionsData[controller.name]) {
			window.intersections = intersections;
			controllerIntersectionsData[controller.name].intersections =
				intersections;

			if (browserName !== 'Safari')
				handleHover(intersections, controller.name);
		}
	};

	const handleTeleportMarker = (controller) => {
		if (controllerIntersectionsData[controller.name.hoveredMarker]) return; // Return if hovering a marker.

		const intersections =
			controllerIntersectionsData[controller.name].intersections;

		const controllerTeleportMarker =
			scene.getObjectByName('XR_Teleport_Marker');
		if (intersections.length > 0) {
			const telePoint = intersections?.[0]?.object?.name === "Navigable_Mesh" ? intersections[0] : null;

			if (telePoint) {
				if (
					controllerIntersectionsData[controller.name]
						.teleportMarker === null
				) {
					controllerIntersectionsData[
						controller.name
					].teleportMarker = telePoint;
				}

				// controllerTeleportMarker.position.copy(intersections[0].point);
				// controllerTeleportMarker.position.x = telePoint.point.x;
				// controllerTeleportMarker.position.z = telePoint.point.z;
				// controllerTeleportMarker.position.y += 0.1;
				controllerTeleportMarker.visible = true;
			}
		}
	};

	const onSelectStart = (event) => {
		const controller = event.target;

		controller.children[0].scale.z = 100;
		controller.userData.selectPressed = true;
		handleHapticFeedback(event, controller);

		if (controllerIntersectionsData[controller.name]) {
			controllerIntersectionsData[controller.name].selectPressed = true;

			const intersections =
				controllerIntersectionsData[controller.name].intersections;

			const sceneObject = getIntersectedMarkerObject(intersections);

			if (sceneObject) {
				const marker =
					sceneObject?.object?.owner ||
					sceneObject?.object?.userData?.owner;
				const object = sceneObject.object || marker;
				controllerIntersectionsData[controller.name].selectedObject =
					object;
				controller.userData.selected = object;

				// Find the boxcollider in the children
				const boxCollider = object.children.find(
					(child) => child.type === 'Mesh',
				);

				if (boxCollider) {
					boxCollider.material.opacity = 1;
				}
			} else {
				handleTeleportMarker(controller);
			}
		}
	};

	const onSelectEnd = (event, onMouseUpCallback) => {
		const controller = event.target;
		controller.children[0].scale.z = 0;
		controller.userData.selectPressed = false;

		if (controllerIntersectionsData[controller.name]) {
			controllerIntersectionsData[controller.name].selectPressed = false;

			const selectedObject =
				controllerIntersectionsData[controller.name].selectedObject ||
				controller.userData.selected;

			if (selectedObject) {
				// Find the boxcollider in the children
				const boxCollider = selectedObject.children.find(
					(child) => child.type === 'Mesh',
				);

				if (boxCollider) {
					boxCollider.material.opacity = 0;
				}

				if (selectedObject.onClick) {
					selectedObject.onClick();
				}

				if (onMouseUpCallback) {
					onMouseUpCallback(
						event,
						selectedObject,
						selectedObject,
						false,
					);
				}

				controllerIntersectionsData[controller.name].selectedObject =
					null;
				controller.userData.selected = null;
			} else {
				if (
					controllerIntersectionsData[controller.name]
						.teleportMarker &&
					dolly.userData.sceneType === '3d_model_scene'
				) {
					const controllerTeleportMarker =
						scene.getObjectByName('XR_Teleport_Marker');
					const INTERSECTION =
						controllerIntersectionsData[controller.name]
							.teleportMarker.point;
					controllerTeleportMarker.visible = false;

					// dolly.position.set(
					// 	INTERSECTION.x,
					// 	INTERSECTION.y,
					// 	INTERSECTION.z,
					// );
					dolly.position.x = INTERSECTION.x;
					dolly.position.z = INTERSECTION.z;
					renderer.xr.setReferenceSpaceType('local-floor');

					controllerIntersectionsData[
						controller.name
					].teleportMarker = null;
				}
			}
		}
	};

	// Haptic Feedback is the control vibration when a button is clicked on.
	const handleHapticFeedback = (event, controller) => {
		console.log('=> event', event);
		if (!event.data) return;
		const gamepad = event.data.gamepad;
		const supportHaptic =
			'hapticActuators' in gamepad &&
			gamepad.hapticActuators != null &&
			gamepad.hapticActuators.length > 0;

		if (supportHaptic && event.data.handedness === controller.name) {
			gamepad.hapticActuators[0].pulse(0.3, 100);
		}
	};

	// Handle joystick movement
	const handleJoystickMovement = () => {
		if (browserName != 'Oculus Browser') return;
		const session = renderer.xr.getSession();
		if (session) {
			for (const source of session.inputSources) {
				if (source.gamepad) {
					const controllerName = source.handedness;
					const gamepad = source.gamepad;

					if (controllerIntersectionsData[controllerName]) {
						if (gamepad.axes[2] == 0) {
							controllerIntersectionsData[
								controllerName
							].isDraggingWithJoystick = false;
						}
						if (
							controllerIntersectionsData[controllerName]
								.isDraggingWithJoystick
						)
							return;
						if (gamepad.axes[2] > 0.5) {
							// Rotate dolly to the right
							controllerIntersectionsData[
								controllerName
							].isDraggingWithJoystick = true;
							dolly.rotation.y -= MathUtils.degToRad(90);
						} else if (gamepad.axes[2] < -0.5) {
							// Rotate dolly to the left
							controllerIntersectionsData[
								controllerName
							].isDraggingWithJoystick = true;
							dolly.rotation.y += MathUtils.degToRad(90);
						}
					}
				}
			}
		}
	};

	// Add Event Listeners
	const addThreeEditorVREventListeners = () => {
		// Add raycasting for camera gaze
		scene.addAnimationHook(
			'AVPCameraGaze_animation-loop',
			handleAVPCameraGaze,
		);

		// Add listener for joystic changes in Oculus
		scene.addAnimationHook(
			'OculusJoystick_animation-loop',
			handleJoystickMovement,
		);

		xrControls.forEach((controller, index) => {
			controller.addEventListener('selectstart', (event) => {
				onSelectStart(event);
			});
			controller.addEventListener('selectend', (event) => {
				onSelectEnd(event, onMouseUpCallback);
			});
			controller.addEventListener('connected', (event) => {
				if (event.data.targetRayMode === 'transient-pointer') {
					controller.name = 'transient-pointer';
				} else {
					controller.name = event.data.handedness;
				}
				// Add render loop hook for this controller in scene
				scene.addAnimationHook(
					'XRcontroller_' + controller.name + '_animation-loop',
					() => {
						handleController(controller);
					},
				);
			});
			controller.addEventListener('disconnected', (event) => {});
		});
		xrGripControls.forEach((gripControl) => {
			// To get info about keys, we can map specific functions to buttons
			gripControl.addEventListener('connected', (event) => {});
		});
		xrHands.forEach((hand) => {
			// To perform an action when the user pinches with the hands
			hand.addEventListener('pinchstart', (event) => {});
			hand.addEventListener('connected', (event) => {
				console.log('Hand connected: ', event.data.targetRayMode);
			});
			hand.addEventListener('disconnected', (event) => {});
			hand.addEventListener('pinchend', (event) => {});
		});

	};

	// Remove event listeners
	const removeThreeEditorVREventListeners = () => {
		// Remove raycasting for camera gaze
		scene.removeAnimationHook('AVPCameraGaze_animation-loop');
		// Remove listener for joystic changes in Oculus
		scene.removeAnimationHook('OculusJoystick_animation-loop');

		xrControls.forEach((controller) => {
			controller.removeEventListener('selectstart', (event) => {
				onSelectStart(event);
			});
			controller.removeEventListener('selectend', (event) => {
				onSelectEnd(event, onMouseUpCallback);
			});
			controller.removeEventListener('connected', (event) => {
				if (event.data.targetRayMode === 'transient-pointer') {
					controller.name = 'transient-pointer';
				} else {
					controller.name = event.data.handedness;
				}
				scene.removeAnimationHook(
					'XR' + controller.name + '_animation-loop',
				);
			});
		});
		xrGripControls.forEach((gripControl) => {
			// To get info about keys, we can map specific functions to buttons
			gripControl.removeEventListener('connected', (event) => {});
		});
		xrHands.forEach((hand) => {
			// To perform an action when the user pinches with the hands
			hand.removeEventListener('pinchstart', (event) => {});
			hand.removeEventListener('connected', (event) => {
				console.log('CONTROL -> Hand connected: ', event);
			});
			hand.addEventListener('disconnected', (event) => {});
			hand.removeEventListener('pinchend', (event) => {});
		});
	};

	return {
		addThreeEditorVREventListeners,
		removeThreeEditorVREventListeners,
	};
};
